import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Clock, CheckCircle2, XCircle, AlertCircle, TrendingUp, Users, FileText, RefreshCw, Loader2, Sparkles, ArrowLeft, Zap } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Buyer / Procurement Manager · Negotiation Dashboard
 * Renders the live summary + shortlist form + rounds history + finalise action
 * for a single tender / quick-quotation.
 *
 * Props:
 *   parentType : 'tender' | 'quick_quotation'
 *   parentId   : string
 */
export default function NegotiationDashboard({ parentType, parentId }) {
  const [state, setState] = useState({ loading: true, exists: false, neg: null });
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  const [me, setMe] = useState(null);
  useEffect(() => { const t = setInterval(() => setTick(x => x + 1), 30000); return () => clearInterval(t); }, []);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setMe(data);
      } catch { /* silent */ }
    })();
  }, []);
  const isDemoUser = !!(me?.is_demo_user || (me?.email || '').toLowerCase().includes('demo'));

  const load = async () => {
    setState(s => ({ ...s, loading: true }));
    try {
      const { data } = await axios.get(`${API}/negotiations/by-parent/${parentType}/${parentId}`, { withCredentials: true });
      setState({ loading: false, exists: !!data.exists, neg: data.exists ? data : null });
    } catch (e) {
      const msg = e.response?.data?.detail || 'Failed to load negotiation';
      toast.error(typeof msg === 'string' ? msg : 'Failed to load negotiation');
      setState({ loading: false, exists: false, neg: null });
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [parentType, parentId]);

  if (state.loading) return <div className="p-6 text-center text-muted-foreground text-sm"><Loader2 className="h-5 w-5 mx-auto animate-spin mb-2" /> Loading negotiation…</div>;
  if (!state.exists) return <StartNegotiationForm parentType={parentType} parentId={parentId} onCreated={load} />;

  const { neg } = state;
  const { summary, suppliers } = neg;
  const currentRound = neg.rounds.find(r => r.round_number === neg.current_round) || {};

  const closeRound = async () => {
    const reason = window.prompt('Reason for closing this round early (optional):') || '';
    if (!window.confirm('Close the current negotiation round now?')) return;
    setBusy(true);
    try {
      await axios.post(`${API}/negotiations/${neg.id}/rounds/close`, { reason }, { withCredentials: true });
      toast.success('Round closed. Non-responders marked; final comparative statement updated.');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to close round'); }
    finally { setBusy(false); }
  };

  const reopen = async () => {
    const reason = window.prompt('Reason for reopening negotiation (mandatory):');
    if (!reason || !reason.trim()) { toast.error('Reason is mandatory.'); return; }
    const deadline = window.prompt('New deadline (YYYY-MM-DDTHH:mm, in your local time):');
    if (!deadline) return;
    setBusy(true);
    try {
      await axios.post(`${API}/negotiations/${neg.id}/reopen`, { reason: reason.trim(), new_deadline_at: deadline }, { withCredentials: true });
      toast.success(`Round ${neg.current_round + 1} started.`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to reopen'); }
    finally { setBusy(false); }
  };

  const finalize = async (supplierId, amount) => {
    const notes = window.prompt('Manager Recommendation notes (optional):') || '';
    if (!window.confirm(`Generate Manager Recommendation for ${suppliers[supplierId]?.company_name || 'supplier'} at ₹${Number(amount).toLocaleString('en-IN')}? This locks the negotiation and prepares the Committee package.`)) return;
    setBusy(true);
    try {
      await axios.post(`${API}/negotiations/${neg.id}/finalize`, { supplier_id: supplierId, amount, manager_notes: notes }, { withCredentials: true });
      toast.success('Manager Recommendation generated. Return to the Tender to send it to the Committee.');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to finalise'); }
    finally { setBusy(false); }
  };

  const demoSimulateAll = async () => {
    if (!window.confirm('Simulate revised quotations from all pending suppliers at ~10% reduction from their original amount?')) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/negotiations/${neg.id}/demo-simulate`,
        { action: 'submit_revised_all', reduction_pct: 10 }, { withCredentials: true });
      toast.success(`Simulated revised quotes for ${data.changed?.length || 0} supplier(s)`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Simulation failed'); }
    finally { setBusy(false); }
  };

  const demoSimulateOne = async (supplierId, reductionPct) => {
    setBusy(true);
    try {
      await axios.post(`${API}/negotiations/${neg.id}/demo-simulate`,
        { action: 'submit_revised_one', supplier_id: supplierId, reduction_pct: reductionPct }, { withCredentials: true });
      toast.success('Simulated revised quote submitted');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Simulation failed'); }
    finally { setBusy(false); }
  };

  const demoDeclineOne = async (supplierId) => {
    setBusy(true);
    try {
      await axios.post(`${API}/negotiations/${neg.id}/demo-simulate`,
        { action: 'decline_one', supplier_id: supplierId }, { withCredentials: true });
      toast.success('Simulated: supplier declined');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Simulation failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="negotiation-dashboard">
      {/* Manager Recommendation summary — only after finalisation */}
      {neg.status === 'finalized' && (
        <div className="p-5 rounded-xl border-2 border-orange/40 bg-gold/10" data-testid="manager-recommendation-card">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="h-5 w-5 text-orange" />
            <div className="text-lg font-bold text-navy">Manager Recommendation Generated</div>
          </div>
          <div className="text-sm text-foreground/80">
            Recommended Supplier: <strong>{suppliers[neg.final_recommended_supplier_id]?.company_name || neg.final_recommended_supplier_id}</strong> · Negotiated Amount: <strong>₹{Number(neg.final_recommended_amount || 0).toLocaleString('en-IN')}</strong>
          </div>
          {neg.manager_notes && <div className="text-xs text-foreground/70 mt-1">Notes: <em>{neg.manager_notes}</em></div>}
          {parentType === 'tender' && (
            <Link to={`/tender/${parentId}`} className="mt-3 inline-flex items-center px-3 py-1.5 rounded bg-navy text-white text-xs font-bold hover:opacity-90" data-testid="back-to-tender-lifecycle-btn">
              <ArrowLeft className="h-3 w-3 mr-1" /> Return to Tender · Send to Committee
            </Link>
          )}
        </div>
      )}

      {/* Live Summary */}
      <div className="bg-white border border-navy/15 rounded-xl overflow-hidden shadow-md">
        <div className="px-5 py-3 bg-navy text-white flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-wider opacity-80">Negotiation Round</div>
            <div className="text-lg font-bold">Round {neg.current_round} · Status: <StatusPill status={neg.status === 'finalized' ? 'finalized' : summary.current_round_status} /></div>
          </div>
          <button onClick={load} className="p-2 rounded hover:bg-white/10" title="Refresh"><RefreshCw className="h-4 w-4" /></button>
        </div>

        <div className="p-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <KPI icon={<Clock className="h-4 w-4 text-gold" />} label="Deadline" value={<DeadlineCell iso={summary.deadline_at} tick={tick} />} testid="kpi-deadline" />
          <KPI icon={<Users className="h-4 w-4 text-gold" />} label="Shortlisted" value={summary.shortlisted_count} testid="kpi-shortlisted" />
          <KPI icon={<CheckCircle2 className="h-4 w-4 text-green-600" />} label="Revised Received" value={summary.revised_received_count} testid="kpi-received" />
          <KPI icon={<AlertCircle className="h-4 w-4 text-orange" />} label="Pending" value={summary.pending_count + summary.not_responded_count} testid="kpi-pending" />
          <KPI icon={<TrendingUp className="h-4 w-4 text-navy" />} label="Recommended (₹)" value={summary.recommended_amount != null ? `₹${Number(summary.recommended_amount).toLocaleString('en-IN')}` : '—'} testid="kpi-recommended" />
          <KPI
            icon={<Sparkles className="h-4 w-4 text-gold" />}
            label="Savings vs Budget"
            value={summary.savings_vs_budget != null
              ? <>₹{Number(summary.savings_vs_budget).toLocaleString('en-IN')} <span className="text-xs text-muted-foreground">({summary.savings_vs_budget_pct}%)</span></>
              : <span className="text-xs text-muted-foreground italic">Budget: Not Available</span>}
            testid="kpi-savings-budget"
          />
          <KPI
            icon={<Sparkles className="h-4 w-4 text-orange" />}
            label="Negotiation Savings vs L1"
            value={summary.negotiation_savings_vs_l1 != null ? <>₹{Number(summary.negotiation_savings_vs_l1).toLocaleString('en-IN')} <span className="text-xs text-muted-foreground">({summary.negotiation_savings_pct}%)</span></> : '—'}
            testid="kpi-savings-l1"
          />
          <KPI icon={<FileText className="h-4 w-4 text-navy" />} label="Progress" value={<ProgressBar percent={summary.progress_percent} />} testid="kpi-progress" />
        </div>

        {/* Actions */}
        {neg.status !== 'finalized' && (
          <div className="px-5 pb-4 flex flex-wrap gap-2">
            {summary.current_round_status === 'active' && (
              <button onClick={closeRound} disabled={busy} className="px-3 py-1.5 rounded bg-red-600 text-white text-xs font-semibold disabled:opacity-50" data-testid="close-round-btn">Close Negotiation</button>
            )}
            {summary.current_round_status === 'closed' && (
              <>
                <button onClick={reopen} disabled={busy} className="px-3 py-1.5 rounded border border-orange text-orange text-xs font-semibold disabled:opacity-50" data-testid="reopen-btn">Reopen Negotiation</button>
              </>
            )}
          </div>
        )}
      </div>

      {/* ────── Demo Simulation Panel (visible only for demo accounts) ────── */}
      {isDemoUser && neg.status !== 'finalized' && summary.current_round_status === 'active' && (
        <div className="bg-gold/10 border-2 border-orange/40 rounded-xl p-4" data-testid="demo-simulation-panel">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Zap className="h-4 w-4 text-orange" />
              <div className="font-bold text-navy text-sm">Hands-On Demo Simulator</div>
              <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-orange text-white">Demo Only</span>
            </div>
            <button
              onClick={demoSimulateAll}
              disabled={busy || summary.pending_count === 0}
              className="px-3 py-1.5 rounded bg-orange text-white text-xs font-bold disabled:opacity-40"
              data-testid="demo-simulate-all-btn"
            >
              <Zap className="h-3 w-3 inline mr-1" /> Auto-Submit Revised Quotes for All Pending Suppliers
            </button>
          </div>
          <p className="text-[11px] text-foreground/70 mb-3">
            Skip the supplier login step. Use these controls to advance the negotiation stage-by-stage inside a single tender: simulate revised quotes, decline, or auto-fill everyone at ~10% reduction.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {(currentRound.shortlisted_supplier_ids || []).map(sid => {
              const q = (currentRound.revised_quotes || []).find(x => x.supplier_id === sid);
              const pending = !q;
              return (
                <div key={sid} className={`p-2 rounded border ${pending ? 'border-orange/40 bg-white' : 'border-border bg-muted/30'}`} data-testid={`demo-sim-row-${sid}`}>
                  <div className="text-xs font-semibold text-navy truncate">{suppliers[sid]?.company_name || sid}</div>
                  <div className="text-[10px] text-muted-foreground mb-1">
                    {q?.status === 'submitted' && `Revised ₹${Number(q.revised_amount).toLocaleString('en-IN')}${q.simulated ? ' · simulated' : ''}`}
                    {q?.status === 'declined' && 'Declined'}
                    {q?.status === 'not_responded' && 'Not Responded'}
                    {pending && 'Awaiting response'}
                  </div>
                  {pending && (
                    <div className="flex gap-1 flex-wrap">
                      <button onClick={() => demoSimulateOne(sid, 8)} disabled={busy} className="text-[10px] px-2 py-1 rounded bg-navy text-white disabled:opacity-40" data-testid={`demo-sim-submit-${sid}`}>Simulate Revised (-8%)</button>
                      <button onClick={() => demoSimulateOne(sid, 12)} disabled={busy} className="text-[10px] px-2 py-1 rounded bg-navy/80 text-white disabled:opacity-40" data-testid={`demo-sim-submit-aggressive-${sid}`}>-12%</button>
                      <button onClick={() => demoDeclineOne(sid)} disabled={busy} className="text-[10px] px-2 py-1 rounded border border-red-500 text-red-600 disabled:opacity-40" data-testid={`demo-sim-decline-${sid}`}>Decline</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Final Comparative Statement */}
      <div className="bg-white border border-border rounded-xl p-5" data-testid="final-comparative-statement">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-navy">Final Comparative Statement</h3>
          <span className="text-xs text-muted-foreground">
            {summary.budget != null && summary.budget > 0
              ? `Budget: ₹${Number(summary.budget).toLocaleString('en-IN')}`
              : 'Budget: Not Available'}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-muted-foreground border-b border-border">
              <tr><th className="text-left py-2">Supplier</th><th className="text-right">Original</th><th className="text-right">Revised</th><th className="text-right">Effective</th><th className="text-left pl-3">Status</th><th></th></tr>
            </thead>
            <tbody>
              {summary.final_comparative_rows.map(row => {
                const isRecommended = row.supplier_id === summary.recommended_supplier_id;
                return (
                  <tr key={row.supplier_id} className={`border-b border-border/60 ${isRecommended ? 'bg-gold/10' : ''}`} data-testid={`fcs-row-${row.supplier_id}`}>
                    <td className="py-2 font-medium">{suppliers[row.supplier_id]?.company_name || row.supplier_id}{isRecommended && <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded bg-gold text-navy">RECOMMENDED</span>}</td>
                    <td className="text-right">{row.original_amount != null ? `₹${Number(row.original_amount).toLocaleString('en-IN')}` : '—'}</td>
                    <td className="text-right">{row.revised_amount != null ? `₹${Number(row.revised_amount).toLocaleString('en-IN')}` : '—'}</td>
                    <td className="text-right font-semibold">{row.effective_amount != null ? `₹${Number(row.effective_amount).toLocaleString('en-IN')}` : '—'}</td>
                    <td className="pl-3"><SupplierResponseBadge status={row.response_status} /></td>
                    <td className="text-right">
                      {neg.status !== 'finalized' && summary.current_round_status === 'closed' && row.effective_amount != null && (
                        <button onClick={() => finalize(row.supplier_id, row.effective_amount)} disabled={busy} className="text-xs px-2 py-1 rounded bg-navy text-white disabled:opacity-50" data-testid={`finalize-${row.supplier_id}`}>Finalise & Generate Manager Recommendation</button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {summary.final_comparative_rows.length === 0 && (
                <tr><td colSpan={6} className="py-4 text-center text-muted-foreground text-sm">No shortlisted suppliers yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Round history */}
      <div className="bg-white border border-border rounded-xl p-5" data-testid="rounds-history">
        <h3 className="font-bold text-navy mb-3">Negotiation History</h3>
        <div className="space-y-3">
          {neg.rounds.map(r => (
            <div key={r.round_number} className="p-3 rounded border border-border" data-testid={`round-${r.round_number}`}>
              <div className="flex items-center justify-between">
                <div className="font-semibold">Round {r.round_number} <StatusPill status={r.status} /></div>
                <div className="text-xs text-muted-foreground">Deadline: {new Date(r.deadline_at).toLocaleString()}</div>
              </div>
              {r.reopen_reason && <div className="text-xs mt-1 text-orange">Reopened: {r.reopen_reason}</div>}
              {r.closed_reason && <div className="text-xs mt-1 text-muted-foreground">Closed: {r.closed_reason}</div>}
              <div className="mt-2 grid sm:grid-cols-3 gap-2 text-xs">
                {r.shortlisted_supplier_ids.map(sid => {
                  const q = (r.revised_quotes || []).find(x => x.supplier_id === sid);
                  return (
                    <div key={sid} className="p-2 rounded bg-muted/30">
                      <div className="font-medium truncate">{suppliers[sid]?.company_name || sid}</div>
                      <div className="text-muted-foreground">
                        {q?.status === 'submitted' && <>Revised: ₹{Number(q.revised_amount).toLocaleString('en-IN')}</>}
                        {q?.status === 'not_responded' && <>Not Responded</>}
                        {q?.status === 'declined' && <>Declined Participation</>}
                        {!q && <>Pending</>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Committee Ready */}
      <div className="bg-white border border-border rounded-xl p-5" data-testid="committee-ready">
        <h3 className="font-bold text-navy mb-3">Committee Package Ready</h3>
        <ul className="space-y-1 text-sm">
          <Check ok={summary.committee_ready.original_comparative_statement} label="Original Comparative Statement" />
          <Check ok={summary.committee_ready.revised_comparative_statement} label="Revised Comparative Statement" />
          <Check ok={summary.committee_ready.negotiation_history} label="Negotiation History" />
          <Check ok={summary.committee_ready.savings_analysis} label="Savings Analysis" />
          <Check ok={summary.committee_ready.manager_notes} label="Procurement Manager Notes" />
        </ul>
        {summary.committee_ready.ready_to_send && <div className="mt-3 px-3 py-1.5 rounded bg-green-100 text-green-800 text-xs font-bold inline-block">✔ Ready to Send</div>}
      </div>
    </div>
  );
}

function StartNegotiationForm({ parentType, parentId, onCreated }) {
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState([]);
  const [deadline, setDeadline] = useState('');
  const [budget, setBudget] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Load candidate suppliers = suppliers with an existing bid on the parent
    (async () => {
      try {
        if (parentType === 'tender') {
          const { data } = await axios.get(`${API}/tenders/${parentId}/comparative-statement`, { withCredentials: true });
          setCandidates((data.bids || []).map(b => ({ id: b.supplier_id, name: b.supplier_name, amount: b.quoted_amount })));
          if (data.estimated_value) setBudget(String(data.estimated_value));
        } else {
          const { data } = await axios.get(`${API}/buyer/quick-quotations/${parentId}`, { withCredentials: true });
          const quotes = data?.supplier_quotes || [];
          setCandidates(quotes.map(q => ({ id: q.supplier_id, name: q.supplier_name || q.supplier_id, amount: q.amount })));
          if (data?.estimated_value) setBudget(String(data.estimated_value));
        }
      } catch (e) { /* silent */ }
    })();
  }, [parentType, parentId]);

  const toggle = (sid) => setSelected(s => s.includes(sid) ? s.filter(x => x !== sid) : [...s, sid]);
  const submit = async () => {
    if (selected.length === 0) { toast.error('Shortlist at least one supplier'); return; }
    if (!deadline) { toast.error('Deadline required'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/negotiations`, {
        parent_type: parentType, parent_id: parentId,
        shortlisted_supplier_ids: selected, deadline_at: deadline,
        budget: budget ? Number(budget) : undefined, notes,
      }, { withCredentials: true });
      toast.success('Negotiation started. Shortlisted suppliers notified.');
      onCreated && onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to start negotiation'); }
    finally { setLoading(false); }
  };

  return (
    <div className="bg-white border border-border rounded-xl p-5" data-testid="start-negotiation-form">
      <h3 className="font-bold text-navy mb-3">Start Negotiation Round</h3>
      <p className="text-xs text-muted-foreground mb-4">Shortlist one or more suppliers, set the deadline, and start the round. Suppliers can submit revised quotations until the deadline; the system auto-closes and generates the Final Comparative Statement.</p>
      <div className="space-y-2 mb-4" data-testid="candidates-list">
        {candidates.length === 0 && <div className="text-sm text-muted-foreground">No bids/quotes found on this procurement yet.</div>}
        {candidates.map(c => (
          <label key={c.id} className={`flex items-center justify-between p-2 rounded border ${selected.includes(c.id) ? 'border-navy bg-navy/5' : 'border-border'} cursor-pointer`} data-testid={`candidate-${c.id}`}>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={selected.includes(c.id)} onChange={() => toggle(c.id)} data-testid={`toggle-${c.id}`} />
              <span className="font-medium">{c.name}</span>
            </div>
            <span className="text-sm">₹{Number(c.amount || 0).toLocaleString('en-IN')}</span>
          </label>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 gap-3 mb-3">
        <div>
          <label className="text-xs font-medium">Negotiation Deadline *</label>
          <input type="datetime-local" value={deadline} onChange={e => setDeadline(e.target.value)} className="w-full mt-1 px-3 py-2 rounded border border-input text-sm" data-testid="deadline-input" />
        </div>
        <div>
          <label className="text-xs font-medium">Approved Budget (₹)</label>
          <input type="number" value={budget} onChange={e => setBudget(e.target.value)} className="w-full mt-1 px-3 py-2 rounded border border-input text-sm" data-testid="budget-input" />
        </div>
      </div>
      <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="Notes to shortlisted suppliers (optional)" className="w-full mb-3 px-3 py-2 rounded border border-input text-sm" data-testid="notes-input" />
      <button onClick={submit} disabled={loading || selected.length === 0} className="px-4 py-2 rounded bg-navy text-white text-sm font-semibold disabled:opacity-50" data-testid="start-negotiation-btn">
        {loading ? 'Starting…' : `Start Negotiation with ${selected.length} supplier${selected.length === 1 ? '' : 's'}`}
      </button>
    </div>
  );
}

// ─── helpers ────────────────────────────────────────────
const KPI = ({ icon, label, value, testid }) => (
  <div className="p-3 rounded-lg border border-border" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1">{icon}{label}</div>
    <div className="mt-1 text-sm font-bold text-navy">{value}</div>
  </div>
);

const ProgressBar = ({ percent }) => (
  <div>
    <div className="w-full h-2 rounded bg-muted overflow-hidden"><div className="h-full bg-navy" style={{ width: `${percent || 0}%` }} /></div>
    <div className="text-[11px] text-muted-foreground mt-1">{percent || 0}% complete</div>
  </div>
);

const StatusPill = ({ status }) => {
  const map = {
    active: 'bg-navy/10 text-navy',
    closed: 'bg-yellow-100 text-yellow-800',
    finalized: 'bg-green-100 text-green-800',
    reopened: 'bg-orange-100 text-orange-800',
  };
  return <span className={`ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded ${map[status] || 'bg-muted'}`}>{(status || '').toUpperCase()}</span>;
};

const SupplierResponseBadge = ({ status }) => {
  const map = {
    submitted: ['bg-green-100 text-green-800', '✔ Submitted'],
    pending: ['bg-yellow-100 text-yellow-800', '⌛ Pending'],
    not_responded: ['bg-red-100 text-red-700', '❌ Not Responded'],
    declined: ['bg-orange-100 text-orange-800', '⊘ Declined'],
  };
  const [cls, label] = map[status] || ['bg-muted', status];
  return <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${cls}`}>{label}</span>;
};

const Check = ({ ok, label }) => (
  <li className="flex items-center gap-2 text-sm" data-testid={`chk-${label.replace(/\s+/g, '-').toLowerCase()}`}>
    {ok ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <XCircle className="h-4 w-4 text-muted-foreground" />}
    <span className={ok ? '' : 'text-muted-foreground'}>{label}</span>
  </li>
);

const DeadlineCell = ({ iso, tick }) => {
  const remaining = useMemo(() => {
    if (!iso) return null;
    const dl = new Date(iso).getTime();
    const now = Date.now();
    const diff = dl - now;
    if (diff <= 0) return 'Deadline passed';
    const d = Math.floor(diff / 86400000);
    const h = Math.floor((diff % 86400000) / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    return `${d}d ${h}h ${m}m`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iso, tick]);
  if (!iso) return '—';
  return (
    <div>
      <div className="text-xs">{new Date(iso).toLocaleString()}</div>
      <div className="text-[11px] text-orange font-semibold" data-testid="deadline-countdown">{remaining}</div>
    </div>
  );
};
